import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";

import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";

/** Webcam capture + file upload with ONE compression pipeline (U20): every
 *  source — a 5 MB phone photo, a webcam frame, a vendor's PNG logo — lands
 *  as a <=800px JPEG q0.85 (~100-200 KB). Used for driver photos, asset
 *  pictures and manufacturer logos; hand-building a second copy is a review
 *  failure. */
export function CameraCapture({ onPhoto, camera = true, uploadLabel = "⭱ Upload photo" }: {
  onPhoto: (blob: Blob) => void;
  /** hide the webcam button (logos are files, not selfies) */
  camera?: boolean;
  uploadLabel?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [live, setLive] = useState(false);
  const [err, setErr] = useState("");

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  };
  useEffect(() => stop, []);          // release the camera on unmount

  const open = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 } }, audio: false,
      });
      streamRef.current = stream;
      setLive(true);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      });
    } catch {
      setErr("Camera unavailable or permission denied — use Upload instead.");
    }
  };

  const toJpeg = (source: CanvasImageSource, w: number, h: number) => {
    const scale = Math.min(1, 800 / w);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff";                    // PNG transparency -> white, not black
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((b) => { if (b) onPhoto(b); }, "image/jpeg", 0.85);
  };

  const shoot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    toJpeg(v, v.videoWidth, v.videoHeight);
    stop();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    try {
      const bmp = await createImageBitmap(f);
      toJpeg(bmp, bmp.width, bmp.height);
      bmp.close();
    } catch {
      setErr("Couldn't read that image file.");
    }
  };

  return (
    <div>
      {live && (
        <video ref={videoRef} muted playsInline
          style={{ width: "100%", maxWidth: 420, borderRadius: 10, border: "1px solid var(--line2)", marginBottom: 10, display: "block" }} />
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {camera && (!live
          ? <button type="button" className="btn ghost sm" onClick={() => void open()}>📷 Open camera</button>
          : <>
              <button type="button" className="btn teal sm" onClick={shoot}>Capture</button>
              <button type="button" className="btn ghost sm" onClick={stop}>Cancel</button>
            </>)}
        <label className="btn ghost sm" style={{ cursor: "pointer" }}>
          {uploadLabel}
          <input type="file" accept="image/jpeg,image/png" style={{ display: "none" }} onChange={onFile} />
        </label>
        {err && <span className="cap">{err}</span>}
      </div>
    </div>
  );
}

/** Current image + capture/upload + "Use this" / "Remove" — the whole panel.
 *  `src` is the current image URL (cache-busted by the caller), `meta` the
 *  caption line under it. */
export function ImageField({ src, meta, onUpload, onRemove, camera = true, size = 140, fit = "cover", empty = "no image" }: {
  src: string | null;
  meta?: ReactNode;
  onUpload: (b: Blob) => Promise<unknown>;
  onRemove?: () => Promise<unknown>;
  camera?: boolean;
  size?: number;
  fit?: "cover" | "contain";
  empty?: string;
}) {
  const fb = useMutationFeedback();
  const [pending, setPending] = useState<Blob | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pending) { setPendingUrl(null); return; }
    const u = URL.createObjectURL(pending);
    setPendingUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [pending]);
  const save = useMutation({
    mutationFn: (b: Blob) => onUpload(b),
    onSuccess: () => { fb.ok(); setPending(null); },
    onError: fb.fail,
  });
  const remove = useMutation({
    mutationFn: () => onRemove!(),
    onSuccess: fb.ok, onError: fb.fail,
  });
  const box: React.CSSProperties = { width: size, height: size, borderRadius: 10, objectFit: fit, background: "var(--surface)" };
  return (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div>
        {pendingUrl ? (
          <img src={pendingUrl} alt="pending" style={{ ...box, border: "2px solid var(--teal)" }} />
        ) : src ? (
          <img src={src} alt="" style={{ ...box, border: "1px solid var(--line2)" }} />
        ) : (
          <div style={{ ...box, border: "1px dashed var(--line2)", display: "flex", alignItems: "center",
            justifyContent: "center", color: "var(--t-dim)", fontSize: 12 }}>{empty}</div>
        )}
        {src && !pending && meta && <div style={{ fontSize: 10.5, color: "var(--t-dim)", marginTop: 4 }}>{meta}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 240 }}>
        <CameraCapture onPhoto={setPending} camera={camera} uploadLabel={camera ? "⭱ Upload photo" : "⭱ Upload image"} />
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
          {pending && <>
            <button type="button" className="btn teal sm" disabled={save.isPending} onClick={() => save.mutate(pending)}>Use this image</button>
            <button type="button" className="btn ghost sm" onClick={() => setPending(null)}>Discard</button>
          </>}
          {!pending && src && onRemove && (
            <button type="button" className="btn ghost sm" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove image</button>
          )}
          {fb.errorNode}
        </div>
      </div>
    </div>
  );
}
